/* eslint-disable no-plusplus */
/* eslint-disable indent */
import * as Sentry from "@sentry/vue";
import { Unsubscribe } from "firebase/firestore";
import { PokerRunEvent } from "@models/PokerRunEvent";
import { EventParticipant } from "@models/EventParticipant";
import EventParticipantContainer from "@/models/EventParticipantContainer";
import { StateTree, defineStore } from "pinia";
import EventParticipantDataService from "@/services/EventParticipantDataService";
import PokerRunEventDataService from "@/services/PokerRunEventDataService";
import { Registration } from "@models/Registration";
import RegistrationDataService from "@/services/RegistrationDataService";
import Constants from "@/models/Constants";
import { Item } from "@models/Item";
import { Stop } from "@models/Stop";
import useDate from "@/modules/useDate";
import { ParticipantStop } from "@models/ParticipantStop";
import { useUserProfileStore } from "./UserProfile";
import { CartItem } from "@models/CartItem";
import CartDataService from "@/services/CartDataService";
import EventUserWaiverDataService from "@/services/EventUserWaiverDataService";
import { EventUserWaiver } from "@models/EventUserWaiver";
import EventTrackerDataService from "@/services/EventTrackerDataService";
import { EventTracker } from "@models/EventTracker";
import { Tracker } from "@models/Tracker";
import { sortUtils } from "@/utils/sortUtils";

const { getDateTime, formatISODate, getDate } = useDate();

const defaultEventParticipant: EventParticipant = {
  eventId: null,
  userId: null,
  brainTreeTransactionId: null,
  extraCardPurchased: null,
  registrationDate: null,
  eventDisclaimerAccpeted: false,
  checkedIn: false,
  hand: null,
  stops: [],
} as unknown as EventParticipant;

const defaultPokerRunEvent: PokerRunEvent = {
  id: null,
  name: null,
  description: null,
  eventType: null,
  eventDate: null,
  fee: null,
  stops: [],
} as unknown as PokerRunEvent;

export const useEventParticipantStore = defineStore("eventParticipant", {
  state: () => ({
    allRegistrations: [] as Registration[],
    ownerRegistrations: [] as Registration[],
    assignedRegistrations: [] as Registration[],
    currentEventParticipants: [] as EventParticipant[],
    currentPokerRunEvent: {
      ...defaultPokerRunEvent,
      stops: [],
    } as PokerRunEvent,
    unsubscribe: null as Unsubscribe | null,
    cartUnsubscribe: null as Unsubscribe | null,
    newCheckin: false,
    newCheckinStop: null as string | null,
    isOffline: false,
    offlineUpdated: false as boolean,
    checkinTimout: null as NodeJS.Timeout | null,
    cartItems: [] as CartItem[],
    userWaiver: null as EventUserWaiver | null,
    participantTrackers: [] as EventTracker[],
    showMissedStopMessage: true as boolean
  }),
  actions: {
    setIsOffline(offline: boolean): void {
      if (this.isOffline !== offline) {
        if (this.checkinTimout) clearTimeout(this.checkinTimout);
        this.isOffline = offline;
        if (this.currentEventParticipants && this.currentEventParticipants.length > 0) {
          if (!offline) {
            if (this.offlineUpdated) {
              const promises = this.currentEventParticipants.map((x: EventParticipant) =>
                EventParticipantDataService.save(x)
              );
              Promise.all(promises).then(() =>
                this.loadEventParticipant(
                  this.currentEventParticipants[0].eventId,
                  this.currentEventParticipants[0].userId
                )
              );
            } else {
              this.loadEventParticipant(
                this.currentEventParticipants[0].eventId,
                this.currentEventParticipants[0].userId
              );
            }
          } else {
            if (
              this.currentPokerRunEvent.eventSettings.cardReleaseType ===
                Constants.CARD_RELEASE_TYPE_SCHEDULE &&
              this.currentEventParticipants.length > 0
            ) {
              this.scheduleCheckinOffine();
            }
          }
        }
      }
    },
    scheduleCheckinOffine(): void {
      const nextStop = this.currentEventParticipants[0].stops.find(
        (x: ParticipantStop) => !x.checkedIn
      );
      if (!nextStop) return;

      const stop: Stop | null =
        (this.currentPokerRunEvent.stops.find((x: Stop) => x.order === nextStop.order) ?? null) as Stop;
      if (!stop) return;
      const now = new Date();
      const expectedArrivalTime = getDateTime(stop.expectedCardDealTime  as string ?? stop.expectedArrivalTime as string);
      const differenceInMilliseconds = expectedArrivalTime.getTime() - now.getTime() + 45000;

      console.log("Scheduling offline checkin in " + differenceInMilliseconds + "ms.");
      this.checkinTimout = setTimeout(() => {
        if (this.isOffline) {
          this.clearNewCheckin();
          this.checkIn((stop as Stop).checkInCode, { offline: true });
          this.scheduleCheckinOffine();
        }
      }, differenceInMilliseconds);
    },
    async reloadRegistrations(profileId: string = ""): Promise<void> {
      const userId = profileId || this.currentEventParticipants[0].userId;
      const registrations = await RegistrationDataService.getByEventUser(
        this.currentPokerRunEvent.id,
        userId
      );
      this.allRegistrations = registrations;
      this.ownerRegistrations = registrations.filter(
        (x) =>
          x.ownerId === profileId && x.lineItems?.some((y) => y.itemType === Constants.FEE_TYPE)
      );
      this.assignedRegistrations = registrations.filter(
        (x) =>
          x.assignedToId === profileId &&
          x.lineItems?.some((y) => y.itemType === Constants.FEE_TYPE)
      );
    },
    async loadEventParticipant(eventId: string, profileId: string): Promise<void> {
      if (this.isOffline) {
        this.setData(this.getData);
        return;
      }

      this.$reset();
      if (this.unsubscribe) {
        this.unsubscribe();
      }

      this.userWaiver = await EventUserWaiverDataService.getWaiver(eventId, profileId);
      const pokerRunEvent: PokerRunEvent = await PokerRunEventDataService.get(eventId);
      Sentry.setContext("event", {
        eventId: eventId,
        eventName: pokerRunEvent.name,
      });
      this.currentPokerRunEvent = pokerRunEvent;

      await this.reloadRegistrations(profileId);

      const unsubscribe = await EventParticipantDataService.getSubscription(
        eventId,
        profileId,
        this.handleEventParticipantChange
      );
      this.unsubscribe = unsubscribe;

      await this.loadCartItems(eventId, profileId);
      this.loadParticipantTrackers();
    },
    handleEventParticipantChange(participant: EventParticipant, changeType: string): void {
      // Make sure we have the full event participant
      if (participant.stops) {
        const registrations = [...this.ownerRegistrations, ...this.assignedRegistrations];
        const eventParticipant = this.currentEventParticipants.find((x) => x.id === participant.id);
        if (eventParticipant) {
          const index = this.currentEventParticipants.indexOf(eventParticipant);
          if (changeType === "removed") {
            this.currentEventParticipants.splice(index, 1);
          } else {
            if (!this.newCheckin) {
              this.checkForNewCheckin(this.currentEventParticipants[index], participant);
            }
            this.currentEventParticipants[index] = participant;
          }
        } else if (registrations.some((x) => x.participantId === participant.id)) {
          this.currentEventParticipants.push(participant);
        }
        this.currentEventParticipants.sort(sortUtils.byTimestampAndId);

        const { profile, setCompetedEvent } = useUserProfileStore();

        if (!profile.completedEvent) {
          const hasCompleted = this.currentEventParticipants.some((x: EventParticipant) =>
            x.stops.every((y: ParticipantStop) => y.checkedIn)
          );
          setCompetedEvent(hasCompleted);
        }

        this.loadParticipantTrackers();
      }
    },
    async loadParticipantTrackers(): Promise<void> {
      const participantIds = this.currentEventParticipants.map((x) => x.id as string);
      if (participantIds.length === 0) return;
      this.participantTrackers = await loadEventTrackers(
        this.currentPokerRunEvent.id as string,
        participantIds
      );
    },
    async loadCartItems(eventId: string, profileId: string): Promise<void> {
      if (this.isOffline) {
        return;
      }

      if (this.cartUnsubscribe) {
        this.cartUnsubscribe();
      }

      this.cartUnsubscribe = await CartDataService.getSubscription(
        eventId,
        profileId,
        this.handleCartChange
      );
    },
    handleCartChange(cartItem: CartItem): void {
      const existingItem = this.cartItems.find((x) => x.cartItemId === cartItem.cartItemId);
      if (!existingItem) {
        this.cartItems.push(cartItem);
      } else {
        const index = this.cartItems.indexOf(existingItem);
        this.cartItems[index] = cartItem;
      }
    },
    async assignParticipantsToExtraCards(
      eventId: string,
      profileId: string,
      participantIds: string[]
    ) {
      const assignedCartItems: CartItem[] = [];
      const unassignedCartItems = this.cartItems.filter((x) => !x.participantId);
      participantIds.forEach((participantId: string, index: number) => {
        unassignedCartItems[index].participantId = participantId;
        assignedCartItems.push({ ...unassignedCartItems[index] });
      });
      await CartDataService.assignParticipantId(eventId, profileId, assignedCartItems);
    },
    clearNewCheckin(): void {
      this.newCheckin = false;
      this.newCheckinStop = null;
    },
    checkForNewCheckin(oldParticipant: EventParticipant, newParticipant: EventParticipant): void {
      if (oldParticipant.hand?.cardSlots && newParticipant.hand?.cardSlots) {
        this.newCheckin =
          oldParticipant.hand?.cardSlots.filter((x) => x.show).length !==
          newParticipant.hand?.cardSlots.filter((x) => x.show).length;
        if (this.newCheckin) {
          const oldCheckin = oldParticipant.stops.filter((x) => x.checkedIn);
          const newCheckin = newParticipant.stops.filter((x) => x.checkedIn);
          const order = newCheckin.find((x) => !oldCheckin.find((y) => y.order === x.order));
          if (order) {
            this.newCheckinStop =
              this.currentPokerRunEvent.stops.find((x: Stop) => x.order === order.order)?.name ??
              null;
          }
        }
      }
    },
    isAlreadyCheckedIn(checkinCode: string): boolean {
      const stop = this.currentPokerRunEvent.stops.find(
        (x: Stop) => x.checkInCode?.toUpperCase() === checkinCode?.toUpperCase()
      );
      return stop !== undefined;
    },
    async checkIn(checkinCode: string, checkinMetadata: any): Promise<boolean> {
      if (
        (!this.userWaiver && this.currentPokerRunEvent.waiver) ||
        (getDate(this.currentPokerRunEvent.eventDate as string) as Date) > new Date()
      ) {
        return false;
      }

      const stop = this.currentPokerRunEvent.stops.find(
        (x: Stop) => x.checkInCode?.toUpperCase() === checkinCode?.toUpperCase()
      );

      if (stop) {
        if (
          !(await hasCheckedInToAllTrackers(
            this.currentPokerRunEvent,
            this.assignedRegistrations.map((x) => x.participantId ?? ""),
            stop
          ))
        ) {
          throw {
            message: "You must check in to all required trackers before checking in to this stop.",
            errorType: "warning",
          };
        }

        if (
          this.currentEventParticipants.every((x) =>
            x.stops.some((y) => y.order === stop.order && y.checkedIn)
          )
        ) {
          throw {
            message: "Your hands have already been dealt cards for this stop.",
            errorType: "warning",
          };
        }

        if (
          this.currentPokerRunEvent.eventSettings.cardReleaseStyle === "strict" &&
          !this.currentEventParticipants.some((x) => {
            const priorStops = x.stops.filter((y) => y.order < stop.order);
            return priorStops.length === 0 || priorStops.every((y) => y.checkedIn);
          })
        ) {
          throw {
            message: "You must check in to all previous stops before checking in to this stop.",
            errorType: "warning",
          };
        }

        const participantContainer = this.getEventParticipantContainer();
        await checkinStop(this, participantContainer, stop, checkinCode, checkinMetadata);
        await this.loadEventParticipant(
          this.currentEventParticipants[0].eventId,
          this.currentEventParticipants[0].userId
        );
        return true;
      } else if (
        this.currentPokerRunEvent?.checkInAllCode.toUpperCase() === checkinCode?.toUpperCase()
      ) {
        const maxStop = this.currentPokerRunEvent.stops.reduce((prev, current) =>
          prev.order > current.order ? prev : current
        );
        const lastStop = this.currentPokerRunEvent.stops.find((x) => x.order === maxStop.order);

        if (lastStop) {
          const participantContainer = this.getEventParticipantContainer();
          await checkinStop(this, participantContainer, lastStop, checkinCode, checkinMetadata);

          await this.loadEventParticipant(
            this.currentEventParticipants[0].eventId,
            this.currentEventParticipants[0].userId
          );
          return true;
        }
      }
      return false;
    },

    async acceptWaiver(): Promise<void> {
      const { profile } = useUserProfileStore();
      this.userWaiver = await EventUserWaiverDataService.save(
        this.currentPokerRunEvent.id,
        profile.id
      );
    },
    getEventParticipantContainer(): EventParticipantContainer {
      const assignedParticipants = this.currentEventParticipants.filter((x) =>
        this.assignedRegistrations.some((y) => y.participantId === x.id)
      );
      return new EventParticipantContainer(assignedParticipants);
    },
    async renameParticipant(eventParticipant: EventParticipant, newName: string): Promise<void> {
      eventParticipant.name = newName;
      await EventParticipantDataService.save(eventParticipant);
    },
    setData(data: any): void {
      this.ownerRegistrations = data.ownerRegistrations;
      this.assignedRegistrations = data.assignedRegistrations;
      this.currentEventParticipants = data.currentEventParticipants;
      this.currentPokerRunEvent = data.currentPokerRunEvent;
    },
    clearMissedStopMessage(): void {
      this.showMissedStopMessage = false;
    },
  },
  getters: {
    isInEvent: (state): boolean => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const eventDate = state.currentPokerRunEvent?.eventDate as string;
      return (
        eventDate !== null &&
        eventDate !== undefined &&
        eventDate.substring(0, 10) === formatISODate(today) &&
        state.currentEventParticipants.length > 0
      );
    },
    canCheckin(state): boolean {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const eventDate = state.currentPokerRunEvent?.eventDate as string;
      return (
        eventDate !== null &&
        eventDate !== undefined &&
        state.currentPokerRunEvent?.status === "active" &&
        eventDate.substring(0, 10) === formatISODate(today)
      );
    },
    hasNextStop(): boolean {
      return this.firstParticipantRecord?.stops.find((x) => x.checkedIn === false) !== undefined;
    },
    firstMissedStop(state): Stop | null {
      if (this.firstParticipantRecord === null) return null;
      if (
        state.currentPokerRunEvent.eventSettings.cardReleaseStyle ===
        Constants.CARD_RELEASE_STYLE_SCAVENGER
      )
        return null;

      const maxCheckedInStop = this.firstParticipantRecord?.stops
        .filter((x) => x.checkedIn)
        .reduce((prev, current) => (prev.order > current.order ? prev : current), { order: 0 });

      const minNotCheckedInStop = this.firstParticipantRecord?.stops
        .filter((x) => !x.checkedIn)
        .reduce((prev, current) => (prev.order < current.order ? prev : current), { order: 1000 });

      if (maxCheckedInStop === undefined || minNotCheckedInStop === undefined) return null;
      if (maxCheckedInStop.order === 0 || minNotCheckedInStop.order === 1000) return null;

      const missedStop = this.currentPokerRunEvent.stops.find(
        (x) => x.order === minNotCheckedInStop.order
      );

      return maxCheckedInStop?.order > minNotCheckedInStop?.order ? missedStop ?? null : null;
    },
    lastStop(state): ParticipantStop | null {
      const firstEventParticipant =
        state.currentEventParticipants && state.currentEventParticipants.length > 0
          ? state.currentEventParticipants[0]
          : null;
      if (firstEventParticipant) {
        const checkedInStops = firstEventParticipant.stops.filter((x) => x.checkedIn);
        return checkedInStops.length > 0 ? checkedInStops[checkedInStops.length - 1] : null;
      }
      return null;
    },
    nextStop(state): Stop | null {
      if (this.lastStop !== null) {
        return state.currentPokerRunEvent.stops.find(
          (x) => x.order === (this.lastStop?.order ?? 0) + 1
        ) as Stop;
      }
      return state.currentPokerRunEvent.stops[0];
    },
    getData(state): any {
      return {
        allRegistrations: state.allRegistrations,
        ownerRegistrations: state.ownerRegistrations,
        assignedRegistrations: state.assignedRegistrations,
        currentEventParticipants: state.currentEventParticipants,
        currentPokerRunEvent: state.currentPokerRunEvent,
      };
    },
    hasOtherItems(state): boolean {
      return (
        this.registrationLineItems.filter((x) => x.itemType === Constants.CUSTOM_TYPE).length > 0
      );
    },
    registrationLineItems(state): Item[] {
      if (!state.allRegistrations) return [];

      // Flatten all line items first
      const allItems = state.allRegistrations.flatMap((x: any) => x.lineItems);

      // Aggregate by itemId
      return Object.values(
        allItems.reduce((acc: Record<string, Item>, item: Item) => {
          if (!acc[item.itemId]) {
            item.quantity = 1;
            acc[item.itemId] = {
              ...item,
            };
          } else {
            acc[item.itemId].quantity = (acc[item.itemId].quantity || 1) + 1;
            acc[item.itemId].amount = (acc[item.itemId].amount || 0) + item.amount;
          }
          return acc;
        }, {})
      );
    },
    availableLineItems(state): Item[] {
      const isCheckedInAt3rdStop = state.currentEventParticipants.every(
        (x) => x.stops?.filter((y: ParticipantStop) => y.checkedIn).length >= 3
      );

      const extraCardCount = state.currentEventParticipants.filter(
        (x: EventParticipant) => x.extraCard
      ).length;

      const today = formatISODate(new Date());
      const customItems = state.currentPokerRunEvent?.lineItems
        ? state.currentPokerRunEvent.lineItems
            ?.filter((x: Item) => x.itemType === Constants.CUSTOM_TYPE)
            .filter((x: Item) => !x.purchaseByDate || x.purchaseByDate >= today)
            .filter(
              (x: Item) => !this.registrationLineItems.some((y: any) => y?.itemId === x.itemId)
            )
        : [];

      const extraCardItems = state.currentPokerRunEvent?.lineItems
        ? state.currentPokerRunEvent.lineItems
            .filter(
              (x: Item) =>
                x.itemType === Constants.EXTRA_CARD_TYPE &&
                state.currentPokerRunEvent.eventSettings.allowExtraCardPurchaseAfterRegistration
            )
            .filter(() => !isCheckedInAt3rdStop)
        : [];

      if (
        extraCardItems.length > 0 &&
        !isCheckedInAt3rdStop &&
        extraCardCount < state.currentEventParticipants.length
      ) {
        extraCardItems[0].availableQuantity = this.assignedRegistrations.filter(
          (y: any) => !y.lineItems?.some((i: any) => i.itemId === extraCardItems[0].itemId)
        ).length;
        return [extraCardItems[0], ...customItems];
      }

      return [...customItems];
    },
    availableAggregatedLineItems(state): Item[] {
      return this.availableLineItems.reduce((acc: Item[], item: Item) => {
        const found = acc.find((x) => x.itemId === item.itemId);
        if (found) {
          found.amount += item.amount;
        } else {
          acc.push({ ...item });
        }
        return acc;
      }, []);
    },
    getSelectedAggregatedLineItems() {
      return (itemIds: string[]): Item[] =>
        this.availableAggregatedLineItems.filter((x: Item) => itemIds.includes(x.itemId));
    },
    getSelectedLineItems() {
      return (itemIds: string[]): Item[] =>
        this.availableLineItems.filter((x: Item) => itemIds.includes(x.itemId));
    },
    canPurchaseAddons(): boolean {
      return this.availableLineItems ? this.availableLineItems.length > 0 : false;
    },
    acceptedWaiver(state): boolean {
      return state.currentEventParticipants.every((x: any) => x.acceptedWaiver);
    },
    canPurchaseExtraCards(state): boolean {
      return state.currentPokerRunEvent.eventSettings?.enableExtraCard &&
        state.currentPokerRunEvent.eventSettings?.allowExtraCardPurchaseAfterRegistration &&
        state.currentEventParticipants.some((x) => x.stops.filter((y) => y.checkedIn).length < 3) &&
        this.availableLineItems
        ? this.availableLineItems.filter((x: any) => x.itemType === Constants.EXTRA_CARD_TYPE)
            .length > 0
        : false;
    },
    firstParticipantRecord(state): EventParticipant | null {
      if (state.currentEventParticipants && state.currentEventParticipants.length > 0) {
        return state.currentEventParticipants[0];
      }
      return null;
    },
  },
  persist: true,
});
const loadEventTrackers = async (eventId: string, participantIds: string[]) => {
  if (participantIds.length === 0) return [];
  return await EventTrackerDataService.getTrackersByParicipantIds(eventId, participantIds);
};

const hasCheckedInToAllTrackers = async (
  pokerRunEvent: PokerRunEvent,
  participantIds: string[],
  stop: Stop
) => {
  const stopTrackers = pokerRunEvent.trackers?.filter((x) => x.requiredStopNumber === stop.order);
  if (pokerRunEvent.eventSettings.cardReleaseStyle != "scavenger") {
    stopTrackers.push(
      ...(pokerRunEvent.trackers?.filter(
        (x) => x.requiredStopNumber && x.requiredStopNumber < stop.order
      ) ?? [])
    );
  }
  if (stopTrackers && stopTrackers.length > 0) {
    const participantTrackers = await loadEventTrackers(pokerRunEvent.id as string, participantIds);
    const uncheckedInTrackers = stopTrackers.filter(
      (x) => !participantTrackers.some((y) => y.checkIns.some((z) => z.trackerId === x.trackerId))
    );

    if (uncheckedInTrackers.length > 0) {
      return false;
    }
  }

  return true;
};

const checkinStop = async (
  store: any,
  participantContainer: EventParticipantContainer,
  stop: Stop,
  checkinCode: string,
  checkinMetadata: any
) => {
  participantContainer.checkIn(checkinCode, store.currentPokerRunEvent, stop, checkinMetadata);

  if (!store.isOffline) {
    const promises = store.currentEventParticipants.map((x: EventParticipant) =>
      EventParticipantDataService.save(x)
    );
    await Promise.all(promises);
  } else {
    store.offlineUpdated = true;
  }

  store.newCheckin = true;
  store.newCheckinStop = stop.name;
};
